// DirectPlaylist.jsx (FILES collection version – no playlistId)
import React, { useState, useContext, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';
import UserContext from '../context/UserContext';
import { api } from '../utils/apiHelper';

const ROUTES = {
  create: '/direct-playlist/create',           // ensure project doc in files collection
  signedUrl: '/direct-playlist/signed-url',    // { url, key }
  saveMeta: '/direct-playlist/save-file-keys', // saves to userMeta.$.playlistsFile[]
  extractor: '/extract-file-metadata',         // <-- real extractor for playlistsFile
};

const sanitize = (s) => s.trim().replace(/\s+/g, '_');
const sanitizeFileName = (name) =>
  name.trim().replace(/\s+/g, '_').replace(/[^\w.-]/g, '');
const detectMediaType = (mime) =>
  mime?.startsWith('audio') ? 'audio' : mime?.startsWith('video') ? 'video' : null;
const slotLetter = (i) => String.fromCharCode(97 + (i % 26)); // a..z

const DirectPlaylist = ({ onSuccess }) => {
  const { authUser } = useContext(UserContext);

  const [projectTitle, setProjectTitle] = useState('');
  const [titleError, setTitleError] = useState('');
  const [projectId, setProjectId] = useState(uuidv4());

  const [files, setFiles] = useState([]); // File objects with preview
  const [uploading, setUploading] = useState(false);
  const [accessType, setAccessType] = useState('private');
  const [mediaType, setMediaType] = useState(null); // 'audio' | 'video'
  const [mixedMedia, setMixedMedia] = useState(false);

  const onDrop = useCallback(
    (accepted) => {
      if (!accepted?.length) return;
      const prepared = accepted.map((f) =>
        Object.assign(f, { preview: URL.createObjectURL(f) })
      );

      const incomingType = detectMediaType(prepared[0]?.type);
      if (!incomingType) {
        alert('Only audio or video files are allowed.');
        return;
      }
      const anyMismatch =
        prepared.some((f) => detectMediaType(f.type) !== incomingType) ||
        (mediaType && mediaType !== incomingType);

      if (anyMismatch) {
        setMixedMedia(true);
        alert(`All files must be ${mediaType || incomingType} files.`);
        return;
      }

      setMixedMedia(false);
      setMediaType((prev) => prev || incomingType);
      setFiles((prev) => [...prev, ...prepared]);
    },
    [mediaType]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  useEffect(() => {
    return () => files.forEach((f) => URL.revokeObjectURL(f.preview));
  }, [files]);

  // 1) Ensure project doc exists (no playlistId returned)
  const ensureProject = async ({ userId, projectId, projectTitle }) => {
    const res = await api(ROUTES.create, 'POST', { userId, projectId, projectTitle });
    if (!res?.success) throw new Error(res?.message || 'Failed to ensure project');
    return true;
  };

  // 2) Get signed PUT URL (no playlistId)
  const getSignedUrl = async ({ userId, projectId, file }) => {
    const safeName = sanitizeFileName(file.name);
    const uniqueName = `${Date.now()}-${uuidv4()}-${safeName}`;
    // backend builds key under: users/{userId}/projects/{projectId}/playlists/{uniqueName}
    const signed = await api(ROUTES.signedUrl, 'POST', {
      userId,
      projectId,
      fileName: uniqueName,
      fileType: file.type,
      accessType,
    });
    if (!signed?.url || !signed?.key) throw new Error('Failed to get signed URL');
    return { ...signed, safeName };
  };

  // 3) PUT to S3
  const uploadOne = async ({ url, file }) => {
    const put = await fetch(url, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
    if (!put.ok) throw new Error(`Failed to upload ${file.name}`);
  };

  // 4) Save metadata into files.playlistsFile[]
  const saveMetadata = async ({ userId, projectId, items, title }) => {
    const res = await api(ROUTES.saveMeta, 'POST', {
      userId,
      projectId,
      projectTitle: title,
      files: items, // [{ key, fileName, url?, projectId, fileId, slotPosition, seqPos, createdAt, accessType, mediaType }]
      accessType,
    });
    if (!res?.success) throw new Error(res?.message || 'Saving metadata failed');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      if (!authUser?._id) throw new Error('You must be signed in');
      const userId = authUser._id;

      const title = sanitize(projectTitle);
      if (!title) {
        setTitleError('Playlist title is required');
        throw new Error('Missing title');
      }
      setTitleError('');

      if (!files.length) throw new Error('No files selected');
      if (mixedMedia) throw new Error(`All files must be ${mediaType} files`);

      const firstType = detectMediaType(files[0]?.type);
      if (!firstType) throw new Error('Only audio or video files are allowed.');
      const anyMismatch = files.some((f) => detectMediaType(f.type) !== firstType);
      if (anyMismatch) throw new Error(`All files must be ${firstType} files`);

      // Ensure project doc (no playlistId flow)
      await ensureProject({ userId, projectId, projectTitle: title });

      // Sign & upload each
      const signedList = await Promise.all(
        files.map((file) => getSignedUrl({ userId, projectId, file }))
      );
      await Promise.all(signedList.map((s, i) => uploadOne({ url: s.url, file: files[i] })));

      // Prepare items for save (include fields your DB expects)
      const nowISO = new Date().toISOString();
      const items = signedList.map((s, i) => ({
        key: s.key,
        fileName: s.safeName,
        url: s.url,               // optional
        accessType,
        projectId,
        fileId: uuidv4(),
        slotPosition: slotLetter(i),
        seqPos: i + 1,
        createdAt: nowISO,
        mediaType: firstType,
      }));

      // Save metadata to files collection (playlistsFile)
      await saveMetadata({ userId, projectId, items, title });

      // Extract durations/frameRates/resolutions into playlistsFile
      await api(ROUTES.extractor, 'POST', { userId, projectId });

      // Reset
      setFiles([]);
      setProjectTitle('');
      setProjectId(uuidv4());
      setMediaType(null);
      setMixedMedia(false);

      onSuccess && onSuccess({ projectId });
      alert('Playlist created and files uploaded.');
    } catch (err) {
      if (err.message !== 'Missing title') alert(err?.message || 'Upload failed');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="direct-playlist">
      <h3>Playlist</h3>

      <form onSubmit={handleSubmit}>
        <div className="mb-2">
          <label><strong>Playlist Title</strong></label>
          <input
            type="text"
            value={projectTitle}
            onChange={(e) => setProjectTitle(e.target.value)}
            placeholder="Enter playlist title"
          />
          {titleError && <p className="error">{titleError}</p>}
        </div>

        <div className="mb-2">
          <label><strong>Access</strong></label>{' '}
          <label style={{ marginRight: 12 }}>
            <input
              type="radio"
              value="private"
              checked={accessType === 'private'}
              onChange={() => setAccessType('private')}
            /> Private
          </label>
          <label>
            <input
              type="radio"
              value="public-read"
              checked={accessType === 'public-read'}
              onChange={() => setAccessType('public-read')}
            /> Public
          </label>
        </div>

        <div {...getRootProps({ className: 'direct-playlist-drop' })}>
          <input {...getInputProps()} />
          {isDragActive ? <p>Drop files here…</p> : <p>Drag & drop audio or video files here, or click to select</p>}
        </div>

        {!!files.length && (
          <ul className="preview-list">
            {files.map((f, i) => (
              <li key={i}>
                <strong>{i + 1}.</strong> {f.name}
                {f.type.startsWith('video') && <div><video src={f.preview} width="220" controls /></div>}
                {f.type.startsWith('audio') && <div><audio src={f.preview} controls /></div>}
              </li>
            ))}
          </ul>
        )}

        <button type="submit" className="dropUploadButton" disabled={uploading}>
          {uploading ? 'Uploading…' : 'Upload Playlist'}
        </button>
      </form>
    </div>
  );
};

export default DirectPlaylist;
